import { useState } from 'react';
import { Link } from 'wouter';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Search, MapPin, Star, ArrowRight, Users } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import type { Business } from '@shared/schema';
import bmzLogoImage from '@assets/327E9CFA-0EB7-454F-93A8-B69ECA8B5641_1753593855527.png';
import { currentBeautyOfTheWeek } from '@/content/beautyOfTheWeek';

export default function HomePage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [searchResults, setSearchResults] = useState<any[] | null>(null);
  const [searchMessage, setSearchMessage] = useState('');

  const { data: businesses = [], isLoading } = useQuery({
    queryKey: ['/api/businesses', { approved: true }],
    queryFn: () => fetch('/api/businesses?approved=true').then(res => res.json()) as Promise<Business[]>
  });

  const { data: scrapedBusinesses = [] } = useQuery({
    queryKey: ['/api/scraped-businesses'],
    queryFn: () => apiRequest('/api/scraped-businesses')
  });

  const locationSearchMutation = useMutation({
    mutationFn: (searchData: { profession: string; city: string; state: string }) =>
      apiRequest('/api/search-location', {
        method: 'POST',
        body: JSON.stringify(searchData),
      }),
    onSuccess: (data: any) => {
      setSearchResults(data.businesses || data || []);
      setSearchMessage(data.message || `Results for "${searchTerm}" in ${city}, ${state}`);
    },
  });

  const stateSearchMutation = useMutation({
    mutationFn: (searchData: { profession: string; state: string }) =>
      apiRequest('/api/search-state', {
        method: 'POST',
        body: JSON.stringify(searchData),
      }),
    onSuccess: (data: any) => {
      setSearchResults(data.businesses || data || []);
      setSearchMessage(data.message || `Results for "${searchTerm}" in ${state}`);
    },
  });

  const nationwideSearchMutation = useMutation({
    mutationFn: (searchData: { profession: string }) =>
      apiRequest('/api/search-nationwide', {
        method: 'POST',
        body: JSON.stringify(searchData),
      }),
    onSuccess: (data: any) => {
      setSearchResults(data.businesses || data || []);
      setSearchMessage(data.message || `Nationwide results for "${searchTerm}"`);
    },
  });

  const handleLocationSearch = () => {
    if (searchTerm && city && state) {
      setSearchResults(null);
      locationSearchMutation.mutate({ profession: searchTerm, city, state });
    }
  };

  const handleStateSearch = () => {
    if (state) {
      setSearchResults(null);
      stateSearchMutation.mutate({ profession: searchTerm, state });
    }
  };

  const handleNationwideSearch = () => {
    setSearchResults(null);
    nationwideSearchMutation.mutate({ profession: searchTerm });
  };

  const clearSearch = () => {
    setSearchResults(null);
    setSearchMessage('');
    setSearchTerm('');
    setCity('');
    setState('');
  };

  const isSearching = locationSearchMutation.isPending || stateSearchMutation.isPending || nationwideSearchMutation.isPending;

  const allBusinesses = [...businesses, ...scrapedBusinesses];
  const filteredBusinesses = searchTerm
    ? allBusinesses.filter(business =>
        (business.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (business.category || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (business.city || '').toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  // Featured businesses from specific cities
  const featuredBusinesses = [
    {
      id: '1',
      name: 'Biscuits and Buns on Banks',
      category: 'Restaurant',
      city: 'New Orleans',
      state: 'LA',
      description: 'Delicious handcrafted biscuits and specialty buns served fresh daily',
      rating: 4.8,
      approved: true,
      ownerId: '',
      createdAt: '',
      address: '',
      zipCode: '',
      phone: '',
      email: '',
      website: '',
      hours: '',
      latitude: 29.9511,
      longitude: -90.0715,
      thumbnail: '/business-thumbnails/biscuits-buns.jpg'
    },
    {
      id: '2',
      name: 'Ivanna\'s Gifted Hands',
      category: 'Beauty & Wellness',
      city: 'Slidell',
      state: 'LA',
      description: 'Expert hair care and styling services with personalized attention',
      rating: 4.9,
      approved: true,
      ownerId: '',
      createdAt: '',
      address: '',
      zipCode: '',
      phone: '',
      email: '',
      website: '',
      hours: '',
      latitude: 30.2752,
      longitude: -89.7811,
      thumbnail: '/business-thumbnails/gifted-hands.png'
    },
    {
      id: '3',
      name: 'Blue Jay Fitness',
      category: 'Fitness & Wellness',
      city: 'Dallas',
      state: 'TX',
      description: 'Premium fitness center with personal training and group classes',
      rating: 4.7,
      approved: true,
      ownerId: '',
      createdAt: '',
      address: '',
      zipCode: '',
      phone: '',
      email: '',
      website: '',
      hours: '',
      latitude: 32.7767,
      longitude: -96.7970,
      thumbnail: '/business-thumbnails/blue-jay-fitness.jpg'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* FBA accent stripe */}
      <div className="fba-stripe" />

      {/* Hero Section */}
      <section className="bg-white py-16 border-b border-gray-100">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-gray-900">
            Find <span className="text-red-700">Black</span> Professionals Nationwide
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl mb-8 text-gray-600">
            Search any profession in any US city or state - lawyers, doctors, contractors, and more
          </p>
          
          {/* Enhanced Search Bar */}
          <div className="max-w-4xl mx-auto bg-white p-4 sm:p-6 rounded-lg shadow-lg">
            <div className="flex flex-col gap-4 mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search for profession (e.g., lawyer, doctor, restaurant)..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600 text-black text-sm sm:text-base"
                />
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-4">
              <div className="flex-1 relative">
                <MapPin className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="City (e.g., New Orleans)"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600 text-black text-sm sm:text-base"
                />
              </div>
              <div className="w-full sm:w-32">
                <input
                  type="text"
                  placeholder="State"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600 text-black text-sm sm:text-base"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <button 
                onClick={handleLocationSearch}
                disabled={!searchTerm || !city || !state || locationSearchMutation.isPending}
                className="px-4 py-3 bg-red-700 text-white rounded-lg hover:bg-red-800 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm sm:text-base sm:px-6"
              >
                {locationSearchMutation.isPending ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    <span className="hidden sm:inline">Searching...</span>
                  </>
                ) : (
                  <>
                    <MapPin className="h-4 w-4" />
                    <span>City Search</span>
                  </>
                )}
              </button>
              
              <button 
                onClick={handleStateSearch}
                disabled={!searchTerm || !state || stateSearchMutation.isPending}
                className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm sm:text-base sm:px-6"
              >
                {stateSearchMutation.isPending ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    <span className="hidden sm:inline">Searching...</span>
                  </>
                ) : (
                  <>
                    <Users className="h-4 w-4" />
                    <span>State Search</span>
                  </>
                )}
              </button>
              
              <button 
                onClick={handleNationwideSearch}
                disabled={!searchTerm || nationwideSearchMutation.isPending}
                className="px-4 py-3 bg-blue-900 text-white rounded-lg hover:bg-blue-950 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm sm:text-base sm:px-6"
              >
                {nationwideSearchMutation.isPending ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    <span className="hidden sm:inline">Searching...</span>
                  </>
                ) : (
                  <>
                    <Search className="h-4 w-4" />
                    <span>Nationwide</span>
                  </>
                )}
              </button>
              
              <Link href="/map" className="block">
                <button className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors text-sm sm:text-base sm:px-6">
                  Browse All
                </button>
              </Link>
            </div>
            
            {searchMessage && searchResults !== null && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between gap-3">
                <p className="text-blue-900 font-medium text-sm">{searchMessage}</p>
                <button onClick={clearSearch} className="text-xs text-gray-500 hover:text-gray-700 underline flex-shrink-0">
                  Clear
                </button>
              </div>
            )}
            {isSearching && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2">
                <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full flex-shrink-0"></div>
                <p className="text-blue-800 text-sm">Searching Yelp for Black-owned businesses...</p>
              </div>
            )}
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center max-w-sm sm:max-w-none mx-auto">
            <Link href="/map" className="w-full sm:w-auto">
              <button className="w-full px-6 sm:px-8 py-3 bg-brand-red hover:bg-red-700 text-white rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2 text-sm sm:text-base">
                <MapPin size={18} className="sm:w-5 sm:h-5" />
                <span>Explore Map</span>
              </button>
            </Link>
            <Link href="/add-business" className="w-full sm:w-auto">
              <button className="w-full px-6 sm:px-8 py-3 bg-blue-900 hover:bg-blue-950 text-white rounded-lg font-semibold transition-colors text-sm sm:text-base">
                Add Your Business
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Businesses */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Featured Businesses
            </h2>
            <p className="text-xl text-gray-600">
              Discover amazing Black-owned businesses in your area
            </p>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-lg shadow-md animate-pulse">
                  <div className="h-48 bg-gray-300 rounded-t-lg"></div>
                  <div className="p-6">
                    <div className="h-4 bg-gray-300 rounded mb-2"></div>
                    <div className="h-3 bg-gray-300 rounded mb-4 w-2/3"></div>
                    <div className="h-3 bg-gray-300 rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : isSearching ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="bg-white rounded-lg shadow-md animate-pulse">
                  <div className="h-48 bg-gray-200 rounded-t-lg"></div>
                  <div className="p-6">
                    <div className="h-4 bg-gray-200 rounded mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded mb-4 w-2/3"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              {/* Determine which list to show */}
              {(() => {
                const displayList = searchResults !== null
                  ? searchResults
                  : searchTerm
                    ? filteredBusinesses
                    : featuredBusinesses;

                if (displayList.length === 0) {
                  return (
                    <div className="text-center py-12">
                      <p className="text-xl text-gray-600">No businesses found matching your search.</p>
                      <button onClick={clearSearch} className="mt-4 text-red-700 underline text-sm">Clear search</button>
                    </div>
                  );
                }

                return (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {displayList.map((business: any, idx: number) => (
                      <Link key={business.id || business.yelpId || idx} href={business.yelpId ? (business.website || '#') : `/business/${business.id}`}>
                        <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer h-full">
                          <div className="h-48 rounded-t-lg flex items-center justify-center overflow-hidden">
                            {business.thumbnail || (business.images && business.images[0]) ? (
                              <img
                                src={business.thumbnail || business.images[0]}
                                alt={business.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="bg-gradient-to-r from-red-700 to-blue-900 w-full h-full flex items-center justify-center">
                                <span className="text-white text-2xl font-bold">
                                  {(business.name || '?').charAt(0)}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="p-6">
                            <h3 className="text-xl font-bold text-gray-900 mb-2">{business.name}</h3>
                            <p className="text-gray-600 mb-2">{business.category}</p>
                            <div className="flex items-center text-gray-500 mb-3">
                              <MapPin size={16} className="mr-1" />
                              <span className="text-sm">{business.city}, {business.state}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center">
                                <Star size={16} className="text-yellow-500 mr-1" />
                                <span className="text-sm text-gray-600">
                                  {business.rating ? Number(business.rating).toFixed(1) : 'No reviews'}
                                  {business.reviewCount ? ` (${business.reviewCount})` : ''}
                                </span>
                              </div>
                              <ArrowRight size={16} className="text-red-700" />
                            </div>
                            {business.phone && (
                              <p className="text-xs text-gray-400 mt-2">{business.phone}</p>
                            )}
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                );
              })()}
            </>
          )}
        </div>
      </section>

      {/* BMZ Section */}
      <section className="py-8 sm:py-12 bg-white">
        <div className="container mx-auto px-4">
          <Link href="/bmz">
            <div className="flex justify-center items-center hover:opacity-90 transition-opacity cursor-pointer">
              <img 
                src={bmzLogoImage} 
                alt="Black Media Zone - BMZ" 
                className="max-w-xs sm:max-w-md w-full h-auto"
              />
            </div>
          </Link>
        </div>
      </section>

      {/* Beauty of the Week - Vintage Jet Magazine Style */}
      <section className="py-12 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <Link href="/beauty">
            <div className="max-w-5xl mx-auto bg-black p-1 shadow-2xl cursor-pointer hover:scale-[1.02] transition-transform duration-300" data-testid="link-botw-teaser">
              <div className="bg-white p-6 sm:p-8">
                {/* Header */}
                <div className="border-b-4 border-black pb-4 mb-6">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                      <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-black tracking-tight">
                        BEAUTY OF THE WEEK
                      </h2>
                      <p className="text-sm sm:text-base text-gray-600 mt-1">
                        {currentBeautyOfTheWeek.weekRange}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs sm:text-sm font-semibold text-brand-red">
                        PRESENTED BY
                      </p>
                      <p className="text-xl sm:text-2xl font-bold text-black">
                        BlackBook
                      </p>
                    </div>
                  </div>
                </div>

                {/* Main Content */}
                <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
                  {/* Photo */}
                  <div className="border-4 border-black shadow-lg">
                    <img 
                      src={currentBeautyOfTheWeek.photoUrl} 
                      alt={currentBeautyOfTheWeek.name}
                      className="w-full h-auto object-cover"
                      data-testid="img-botw-featured"
                    />
                  </div>

                  {/* Info */}
                  <div className="flex flex-col justify-center space-y-4 sm:space-y-6">
                    <div>
                      <h3 className="text-3xl sm:text-4xl md:text-5xl font-bold text-black mb-2 tracking-tight">
                        {currentBeautyOfTheWeek.name}
                      </h3>
                      <div className="border-t-2 border-brand-blue w-24 my-3"></div>
                    </div>

                    <div className="space-y-2 text-base sm:text-lg">
                      <p className="text-gray-800">
                        <span className="font-bold text-black">Age:</span> {currentBeautyOfTheWeek.age}
                      </p>
                      <p className="text-gray-800">
                        <span className="font-bold text-black">Hometown:</span> {currentBeautyOfTheWeek.hometown}
                      </p>
                      <p className="text-gray-800">
                        <span className="font-bold text-black">School:</span> {currentBeautyOfTheWeek.school}
                      </p>
                      <p className="text-gray-800">
                        <span className="font-bold text-black">Major:</span> {currentBeautyOfTheWeek.yearMajor}
                      </p>
                    </div>

                    <div className="border-l-4 border-brand-red pl-4 py-2">
                      <p className="text-sm sm:text-base text-gray-700 italic leading-relaxed">
                        "{currentBeautyOfTheWeek.tagline}"
                      </p>
                    </div>

                    <div className="pt-4">
                      <div className="inline-block bg-black text-white px-6 py-3 font-bold text-sm sm:text-base hover:bg-brand-red transition-colors">
                        SEE FULL SPREAD →
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="mt-6 pt-4 border-t-2 border-gray-200 text-center">
                  <p className="text-xs sm:text-sm text-gray-600 font-medium">
                    CELEBRATING BLACK EXCELLENCE • REPRESENTATION MATTERS
                  </p>
                </div>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* Original Black Book Promotion Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-lg text-center border-2 border-brand-red">
              <div className="text-brand-red text-5xl mb-4">
                <Search size={48} className="mx-auto" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Find Businesses</h3>
              <p className="text-gray-600">Search for Black-owned businesses by location, category, or name</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg text-center border-2 border-brand-blue">
              <div className="text-brand-blue text-5xl mb-4">
                <Users size={48} className="mx-auto" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Official Merchandise</h3>
              <p className="text-gray-600">Support the movement with our premium BLK Crown collection</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg text-center border-2 border-brand-red">
              <div className="text-brand-red text-5xl mb-4">
                <MapPin size={48} className="mx-auto" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Black Media Zone</h3>
              <p className="text-gray-600">Stay informed with news and stories from our community</p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-brand-black text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Support Black-Owned Businesses?
          </h2>
          <p className="text-xl mb-8 text-gray-300">
            Join our community and help local businesses thrive
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center max-w-sm sm:max-w-none mx-auto">
            <Link href="/map" className="w-full sm:w-auto">
              <button className="w-full px-6 sm:px-8 py-3 bg-brand-red hover:bg-red-700 text-white rounded-lg font-semibold transition-colors text-sm sm:text-base">
                Find Businesses Near You
              </button>
            </Link>
            <Link href="/add-business" className="w-full sm:w-auto">
              <button className="w-full px-6 sm:px-8 py-3 border-2 border-brand-blue text-brand-blue hover:bg-brand-blue hover:text-black rounded-lg font-semibold transition-colors text-sm sm:text-base">
                List Your Business
              </button>
            </Link>
          </div>
          

        </div>
      </section>
    </div>
  );
}